import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { generateID } from '@domain-group/javascript-utils'
import {
  Box, Button, FormField, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import useToggleListItem from '@hooks/useToggleListItem'

import EntitySelector from '@components/EntitySelector'
import OrganizationSelector from '@components/OrganizationSelector'
import ToggleListItem from '@components/ToggleListItem'

interface ProjectOptionsProps {
  callbacks: {
    setEntityState: (fields: { [key: string]: any }) => void,
  },
  entityState: {
    ad_hoc: boolean,
    brand_id: number,
    checklist_template_id: number,
    create_master_suite_section: boolean,
    debtor_id: number,
    debtor_type: string,
    due_date: string,
  },
}

const ProjectOptions = (props: ProjectOptionsProps) => {
  const {
    callbacks: { setEntityState },
    entityState: {
      ad_hoc,
      brand_id,
      checklist_template_id,
      create_master_suite_section,
      debtor_id,
      debtor_type,
      due_date,
    },
  } = props

  const toggleListItemPayload = useToggleListItem({
    callbacks: {
      checkItemExistsFn: (value, array) => (
        array.some((existingItem: { title: string }) => existingItem.title === value)
      ),
      itemsUpdatedFn: array => setEntityState({
        task_list_section_titles: array.map(item => item.title),
      }),
    },
    defaultItems: [
      {
        key: 'template-tasks',
        title: 'Template Tasks',
      },
    ],
    toggleArrayOptions: {
      deepCompare: true,
    },
  })

  const {
    callbacks: {
      setState,
      toggleItem,
    },
    hasItems,
    inputValue,
    itemExists,
    items,
  } = toggleListItemPayload

  return (
    <>
      <ToggleListItem
        onClick={() => setEntityState({
          ad_hoc: !ad_hoc,
          create_master_suite_section: !!ad_hoc,
        })}
        toggleValue={ad_hoc}
      >
        <ToggleListItem.Title>Ad Hoc</ToggleListItem.Title>
        <ToggleListItem.Description>
          For on demand templates that are not part of a long running project.
        </ToggleListItem.Description>
      </ToggleListItem>

      {!ad_hoc && (
        <ToggleListItem
          onClick={() => setEntityState({ create_master_suite_section: !create_master_suite_section })}
          toggleValue={create_master_suite_section}
        >
          <ToggleListItem.Title>Create Master Suite Group</ToggleListItem.Title>
          <ToggleListItem.Description>
            A new group for the Master Suite will created along with the additional project groups.
          </ToggleListItem.Description>
        </ToggleListItem>
      )}

      <SidebarNoticeBox boxProps={{ marginBottom: 0 }}>
        <SidebarNoticeBox.Title>
          Corporate Agency as Debtor
        </SidebarNoticeBox.Title>

        <SidebarNoticeBox.Paragraph>
          When assigning a debtor, please use the Corporate Agency instead of the Brand.
        </SidebarNoticeBox.Paragraph>
      </SidebarNoticeBox>

      <Box flexDirection="column" paddingTop="large">
        <FormField label="Debtor Type" boxProps={{ marginBottom: debtor_type ? 'large' : 0 }}>
          <select
            name="debtor_type"
            value={debtor_type}
            onChange={e => setEntityState({ debtor_id: undefined, debtor_type: e.target.value })}
          >
            <option value="">Please Select...</option>
            <option value="Agency">Agency</option>
            <option value="Provider">Provider</option>
          </select>
        </FormField>

        {!!debtor_type && (
          <OrganizationSelector
            callbacks={{
              // @ts-expect-error TS2339
              selectItem: ({ entity }) => setEntityState({ debtor_id: entity?.id }),
            }}
            // @ts-expect-error TS2322
            entityType={debtor_type}
            label="Debtor"
            searchRequestOptions={{ billable: true, brand_id }}
            selectedItemId={debtor_id}
          />
        )}
      </Box>

      <Box flexDirection="column" paddingTop="large">
        <EntitySelector
          // @ts-expect-error TS2741
          callbacks={{
            selectItem: taskListTemplate => setEntityState({
              checklist_template_id: taskListTemplate?.id,
            }),
          }}
          label="* Checklist Template"
          entityKey="taskListTemplates"
          searchRequestOptions={{
            include_global_task_list_templates: true,
            owner_type: brand_id ? 'Brand' : undefined,
            owner_id: brand_id,
            template_checklist: true,
          }}
          selectedItemId={checklist_template_id}
        />

        {!ad_hoc && (
          <>
            <FormField label="Due Date" marginTop="large">
              <input
                name="due_date"
                onChange={e => setEntityState({ due_date: e.target.value })}
                type="date"
                value={due_date}
              />
            </FormField>

            <FormField label="Project Groups" marginTop="large">
              <Box>
                <input value={inputValue} onChange={e => setState({ inputValue: e.target.value })} type="text" />

                <Button
                  buttonStyle={itemExists ? 'secondaryDestroy' : 'secondaryCreate'}
                  onClick={() => toggleItem({ key: generateID(), title: inputValue })}
                  // @ts-expect-error TS2322
                  marginLeft="small"
                  size="small"
                  width="auto"
                >
                  {itemExists ? 'Remove' : 'Add'}
                </Button>
              </Box>

              {hasItems && (
                <Box flexDirection="column" marginTop="medium">
                  {items.map(item => (
                    <Box
                      border="1px solid"
                      borderColor="lineColor"
                      borderRadius="5px"
                      fontSize="small"
                      justifyContent="space-between"
                      key={item.key}
                      marginBottom="small"
                      padding="medium"
                    >
                      <Text>{item.title}</Text>

                      <Text color="bodyFontLightColor" onClick={() => toggleItem(item)}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </Text>
                    </Box>
                  ))}
                </Box>
              )}
            </FormField>
          </>
        )}
      </Box>
    </>
  )
}

export default ProjectOptions
